import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatDkk } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 15; // 15 * 2s = 30s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", tx: null });
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", tx: null });
      return;
    }
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", tx: data });
          setTimeout(() => {
            if (data.problem_id) navigate(`/forespoergsler/${data.problem_id}`);
          }, 1500);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed", tx: data });
          return;
        }
      } catch (e) {
        // keep polling
      }
      if (attempts.current >= MAX_POLLS) {
        setState({ status: "timeout", tx: null });
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId, navigate]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 sm:py-24 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="mx-auto text-brand-green animate-spin" size={40} />
          <h1 className="mt-4 text-xl sm:text-2xl font-semibold text-brand-ink">Bekræfter din betaling…</h1>
          <p className="mt-2 text-sm text-brand-body">Vi registrerer betalingen og åbner tidsaftalen.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-[#166534]" size={48} />
          <h1 className="mt-4 text-xl sm:text-2xl font-semibold text-brand-ink" data-testid="payment-success-title">Betaling gennemført</h1>
          {state.tx?.amount_dkk && (
            <p className="mt-1 text-sm text-brand-body">Beløb: <span className="font-mono">{formatDkk(state.tx.amount_dkk)}</span></p>
          )}
          <p className="mt-3 text-sm text-brand-body">Sender dig tilbage til opgaven, så I kan aftale tidspunkt…</p>
          {state.tx?.problem_id && (
            <Link
              to={`/forespoergsler/${state.tx.problem_id}`}
              className="btn-primary mt-4 inline-block !py-2 text-sm"
              data-testid="goto-request-btn"
            >
              Aftal tidspunkt
            </Link>
          )}
        </>
      )}
      {state.status === "failed" && (
        <>
          <XCircle className="mx-auto text-[#B91C1C]" size={48} />
          <h1 className="mt-4 text-xl sm:text-2xl font-semibold text-brand-ink">Betalingen mislykkedes</h1>
          <p className="mt-2 text-sm text-brand-body">Der blev ikke gennemført en betaling. Du kan prøve igen fra opgaven.</p>
          <Link to="/dashboard" className="btn-primary mt-4 inline-block !py-2 text-sm">Til mine forespørgsler</Link>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <Loader2 className="mx-auto text-[#B0561D]" size={40} />
          <h1 className="mt-4 text-xl sm:text-2xl font-semibold text-brand-ink">Vi venter stadig på Stripe</h1>
          <p className="mt-2 text-sm text-brand-body">Din betaling ser ud til at være i gang. Tjek dashboardet om et øjeblik.</p>
          <Link to="/dashboard" className="btn-primary mt-4 inline-block !py-2 text-sm">Til dashboard</Link>
        </>
      )}
      {state.status === "error" && (
        <>
          <XCircle className="mx-auto text-[#B91C1C]" size={48} />
          <h1 className="mt-4 text-xl sm:text-2xl font-semibold text-brand-ink">Manglende betalingsreference</h1>
          <Link to="/dashboard" className="btn-primary mt-4 inline-block !py-2 text-sm">Til dashboard</Link>
        </>
      )}
    </div>
  );
}

